import {
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";
import {
  addCallParticipantAiCall,
  callOfflineParticipantAiCall,
  createGroupCallApiCall,
  deleteCallAPICall,
  getCallDetailsApiCall,
  getCallsForCurrentUserApiCall,
  speakAudioAPICall,
  transcribeAPICall,
  kickParticipantApiCall,
  muteParticipantApiCall,
  muteAllParticipantsApiCall,
  updateMuteStateApiCall,
  reinviteParticipantApiCall,
  makeCallAdminApiCall,
  revokeCallAdminApiCall,
} from "../API/calls/callsService";
import moment from "moment";
import LocalStorage from "../Helpers/LocalStorage";
import { NORMAL_BG } from "../Constants/constantValues";

export const CallsContext = createContext({
  calls: [],
  callsRef: [],
  incomingCallRef: [],
  answeredCallUUIDRef: [],
  activeCallRef: [],
  incomingCallDetails: [],
  selectedCallAccess: [],
  selectedCall: [],
  selectedAudioInput: [],
  selectedVideoInput: [],
  selectedAudioOutput: [],
  backgroundType: [],
  remoteNoiseFilterEnabled: true,
  answeredCallUUID: [],
  createGroupCall: () => { },
  getCalls: () => { },
  getCallDetails: () => { },
  deleteCall: () => { },
  transcribeAudio: () => { },
  speakAudio: () => { },
  setCalls: () => { },
  addCallParticipant: () => { },
  callOfflineParticipant: () => { },
  setIncomingCallDetails: () => { },
  setSelectedCallAccess: () => { },
  setSelectedCall: () => { },
  setSelectedAudioInput: () => { },
  setSelectedVideoInput: () => { },
  setSelectedAudioOutput: () => { },
  setBackgoundType: () => { },
  setRemoteNoiseFilterEnabled: () => { },
  setAnsweredCallUUID: () => { },
  handleSetAsnweredCallUUID: () => { },
});

const CallsContextProvider = ({ children }) => {
  const [calls, setCalls] = useState([]);
  const [incomingCallDetails, setIncomingCallDetails] = useState(null);
  const [selectedCallAccess, setSelectedCallAccess] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [answeredCallUUID, setAnsweredCallUUID] = useState(null);

  const [selectedAudioInput, setSelectedAudioInput] = useState(
    LocalStorage.getInputDevice()
  );

  const [selectedVideoInput, setSelectedVideoInput] = useState(
    LocalStorage.getVideoInputDevice()
  );

  const [selectedAudioOutput, setSelectedAudioOutput] = useState(
    LocalStorage.getOutputDevice()
  );

  const [backgroundType, setBackgoundType] = useState(NORMAL_BG);

  const [remoteNoiseFilterEnabled, setRemoteNoiseFilterEnabled] = useState(
    LocalStorage.getRemoteNoiseFilterEnabled()
  );

  const callsRef = useRef(null);
  const activeCallRef = useRef(null);
  const incomingCallRef = useRef(null);
  const answeredCallUUIDRef = useRef(null);
  const handleCallsResponseFnRef = useRef(() => {});


  const handleSetAsnweredCallUUID = useCallback((uuid) => {
    answeredCallUUIDRef.current = uuid
    setAnsweredCallUUID(uuid)
  }, []);

  const createGroupCall = useCallback(
    async (users_id, group_id, video, onSuccess, errorCallback) => {
      try {
        await createGroupCallApiCall(
          users_id,
          group_id,
          video,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const callOfflineParticipant = useCallback(
    async (user_id, call_id, onSuccess, errorCallback) => {
      try {
        await callOfflineParticipantAiCall(
          user_id,
          call_id,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const addCallParticipant = useCallback(
    async (users_id, call_id, onSuccess, errorCallback) => {
      try {
        await addCallParticipantAiCall(
          users_id,
          call_id,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const getCallDetails = useCallback(async (uuid, onSuccess, errorCallback) => {
    try {
      await getCallDetailsApiCall(
        uuid,
        function (response) {
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, []);

  const getCalls = useCallback(async (onSuccess, errorCallback) => {
    try {
      await getCallsForCurrentUserApiCall(
        function (response) {
          handleCallsResponseFnRef.current(response);
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, []);

  const handleCallsResponse = useCallback((response) => {
    const sortedCalls = response.data.calls.sort((a, b) =>
      moment(a.created_at).isBefore(moment(b.created_at)) ? 1 : -1
    );
    callsRef.current = sortedCalls;
    setCalls(sortedCalls);
  }, []);
  handleCallsResponseFnRef.current = handleCallsResponse;

const deleteCall = useCallback((callid, onSuccess, errorCallback) => {
  try {
    deleteCallAPICall(
      callid,
      (response) => onSuccess && onSuccess(response),
      (error) => errorCallback && errorCallback(error)
    );
  } catch (err) {
    if (errorCallback) errorCallback(err);
  }
}, []);
  const transcribeAudio = useCallback(
    async (formData, onSuccess, errorCallback) => {
      try {
        await transcribeAPICall(
          formData,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );
  const speakAudio = useCallback(async (text, to, onSuccess, errorCallback, speakerUserId) => {
    try {
      await speakAudioAPICall(
        text,
        to,
        function (response) {
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        },
        speakerUserId
      );
    } catch (err) {
      errorCallback(err);
    }
  }, []);

  const kickParticipant = useCallback((callId, userId, onSuccess, errorCallback) => {
    kickParticipantApiCall(callId, userId, onSuccess, errorCallback);
  }, []);

  const muteParticipant = useCallback((callId, userId, onSuccess, errorCallback) => {
    muteParticipantApiCall(callId, userId, onSuccess, errorCallback);
  }, []);

  const muteAllParticipants = useCallback((callId, onSuccess, errorCallback) => {
    muteAllParticipantsApiCall(callId, onSuccess, errorCallback);
  }, []);

  const updateMuteState = useCallback((callId, muted, onSuccess, errorCallback) => {
    updateMuteStateApiCall(callId, muted, onSuccess, errorCallback);
  }, []);

  const reinviteParticipant = useCallback((callId, userId, onSuccess, errorCallback) => {
    reinviteParticipantApiCall(callId, userId, onSuccess, errorCallback);
  }, []);

  const makeCallAdmin = useCallback((callId, userId, onSuccess, errorCallback) => {
    makeCallAdminApiCall(callId, userId, onSuccess, errorCallback);
  }, []);

  const revokeCallAdmin = useCallback((callId, userId, onSuccess, errorCallback) => {
    revokeCallAdminApiCall(callId, userId, onSuccess, errorCallback);
  }, []);

  const contextValue = useMemo(
    () => ({
      calls,
      callsRef,
      activeCallRef,
      answeredCallUUIDRef,
      incomingCallDetails,
      getCalls,
      getCallDetails,
      deleteCall,
      transcribeAudio,
      speakAudio,
      setCalls,
      createGroupCall,
      addCallParticipant,
      callOfflineParticipant,
      setIncomingCallDetails,
      selectedCallAccess,
      setSelectedCallAccess,
      selectedCall,
      setSelectedCall,
      incomingCallRef,
      selectedAudioInput,
      setSelectedAudioInput,
      selectedVideoInput,
      setSelectedVideoInput,
      selectedAudioOutput,
      setSelectedAudioOutput,
      backgroundType,
      setBackgoundType,
      remoteNoiseFilterEnabled,
      setRemoteNoiseFilterEnabled,
      answeredCallUUID,
      setAnsweredCallUUID,
      handleSetAsnweredCallUUID,
      kickParticipant,
      muteParticipant,
      muteAllParticipants,
      updateMuteState,
      reinviteParticipant,
      makeCallAdmin,
      revokeCallAdmin,
    }),
    [
      calls,
      callsRef,
      activeCallRef,
      answeredCallUUIDRef,
      incomingCallDetails,
      getCalls,
      getCallDetails,
      deleteCall,
      transcribeAudio,
      speakAudio,
      setCalls,
      createGroupCall,
      addCallParticipant,
      callOfflineParticipant,
      setIncomingCallDetails,
      selectedCallAccess,
      setSelectedCallAccess,
      selectedCall,
      setSelectedCall,
      incomingCallRef,
      selectedAudioInput,
      setSelectedAudioInput,
      selectedVideoInput,
      setSelectedVideoInput,
      selectedAudioOutput,
      setSelectedAudioOutput,
      backgroundType,
      setBackgoundType,
      remoteNoiseFilterEnabled,
      setRemoteNoiseFilterEnabled,
      answeredCallUUID,
      setAnsweredCallUUID,
      handleSetAsnweredCallUUID,
      kickParticipant,
      muteParticipant,
      muteAllParticipants,
      updateMuteState,
      reinviteParticipant,
      makeCallAdmin,
      revokeCallAdmin,
    ]
  );

  return (
    <CallsContext.Provider value={contextValue}>
      {children}
    </CallsContext.Provider>
  );
};

export const useCallsService = () => useContext(CallsContext);

export default CallsContextProvider;
